import { Result } from 'ts-res';
import { ConfigurationDataType, RuleType } from 'src/clientConfiguration';
import { KameleoonError } from 'src/kameleoonError';
import { KameleoonData } from 'src/kameleoonData';
import { TrackingStatus } from 'src/types';
import { TargetingDataType } from 'src/targeting';

export type ClientDataType = {
  data: ConfigurationDataType;
  lastUpdate: string;
  lastModified?: string;
  requestTime?: number;
};

export type DataInfoType = {
  // --- Note ---
  // Not a real next visit time, but a minimum time that guarantees to make it to the next visit
  nextVisitTime: number;
  nextDataCleanup?: number;
  unsentData: {
    [visitorCode: string]: KameleoonData[];
  };
  mappingIdentifiers?: {
    [visitorCode: string]: MappingIdentifierType;
  };
};

export type MappingIdentifierType = {
  mappingIdentifier: string;
  timestamp: number;
};

export type FeatureFlagVariationType = {
  variationId: number;
  isTargetedRule: boolean;
  status: TrackingStatus;
};

export type FeatureFlagVariationsType = {
  [experimentId: string]: FeatureFlagVariationType;
};

export type VariationDataType = {
  [visitorCode: string]: FeatureFlagVariationsType;
};

export type ForcedFeatureVariationType = {
  featureKey: string;
  rule: ForcedVariationRuleType | null;
  varByExp: ForcedVariationVarByExpType | null;
  simulated: boolean;
};

export type ForcedExperimentVariationType = {
  rule: ForcedVariationRuleType;
  varByExp: ForcedVariationVarByExpType;
  forceTargeting: boolean;
};

export type ForcedVariationRuleType = {
  type: RuleType;
  experimentId: number;
};

export type ForcedVariationVarByExpType = {
  variationKey: string;
  variationId: number;
};

export type SimulatedVariationType = {
  featureKey: string;
  variationId: number | null;
  experimentId: number;
};

export type ForcedExperimentVariationDataType = {
  [visitorCode: string]: {
    [experimentId: number]: ForcedExperimentVariationType;
  };
};

export type ForcedFeatureVariationDataType = {
  [simulated: number]: {
    [visitorCode: string]: {
      [featureKey: string]: ForcedFeatureVariationType;
    };
  };
};

export type ForcedVariationDataType = {
  [visitorCode: string]: {
    [featureKey: string]: ForcedFeatureVariationType;
  };
};

export enum LegalConsent {
  Unknown = 0,
  Given = 1,
  NotGiven = 2,
}

export type LegalConsentDataType =
  | boolean
  | {
      consent: LegalConsent;
    }
  | {
      consent: boolean;
    };

// for consistency with the old data in the storage
export type StorageConsentDataType = {
  [visitorCode: string]: LegalConsentDataType;
};

export type ScheduledVisitorsType = {
  [visitorCode: string]: {
    isConsentProvided: boolean;
  };
};

export type TrackingDataType = {
  scheduledVisitors: ScheduledVisitorsType;
};

/**
 * @type `StorageDataType` - an enumeration type which represents a data stored on SDK storage
 *
 * `string` represents visitor code storage with only one string value
 * */
export type StorageDataType =
  | ClientDataType
  | TargetingDataType
  | TrackingDataType
  | VariationDataType
  | StorageConsentDataType
  | DataInfoType
  | string;

/**
 * @interface an interface of External Storage which must be implemented to be used as external storage an SDK
 * */
export interface IExternalStorage<T = StorageDataType> {
  /**
   * @method read - reads data from the storage
   * @param {string} key - a key, which different SDK data blocks are stored under
   * @returns {T | null} representation of generic Kameleoon data type `T` or `null` if the storage was read successfully but it's empty
   * */
  read: (key: string) => T | null;
  /**
   * @method write - writes data to storage
   * @param {string} key - a key, which different SDK data blocks are stored under
   * @param {T} data - representation of generic Kameleoon data type
   * @returns {void}
   * */
  write: (key: string, data: T) => void;
}

/**
 * @interface an interface which serves as a wrapper over `IExternalStorage` for internal SDK usage
 * */
export interface IStorage<T> {
  read: () => Result<T, KameleoonError>;
  write: (data: T) => Result<void, KameleoonError>;
  update: (data: Partial<T>) => Result<void, KameleoonError>;
}
